import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminBookingAction,
  addBlockedTime,
  addDayOff,
  createService,
  createTherapist,
  fetchAdminBookings,
  fetchAdminPayments,
  fetchAdminTherapists,
  fetchDashboard,
  fetchReport,
  fetchSchedule,
  fetchServices,
  fetchTenantSettings,
  removeBlockedTime,
  removeDayOff,
  saveWeeklySchedule,
  updatePaymentConfig,
  updateService,
  updateTenantSettings,
  updateTherapist,
  type AdminBooking,
  type AdminService,
  type AdminTherapist,
  type Dashboard,
} from "../api/admin";
import { ApiError } from "../api/client";
import { Button, Card, EmptyState, ErrorState, Field, PageHeader, Spinner } from "../components/ui";
import { StatusPill } from "../components/status";
import { GenderTag } from "../components/gender";
import { StarDisplay } from "../components/rating";
import { TherapistReviewsPanel } from "../components/TherapistReviewsPanel";
import { formatDateLong, formatDateShort, formatIDR } from "../lib/format";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const BOOKING_STATUSES = [
  "PENDING_PAYMENT",
  "CONFIRMED",
  "CHECKED_IN",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
  "NO_SHOW",
];

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="stat-card">
      <div className="v">{typeof value === "number" ? value.toLocaleString("id-ID") : value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

// ─────────────────────────── Dashboard ───────────────────────────
export function AdminDashboardPage() {
  const dashQuery = useQuery({ queryKey: ["admin", "dashboard"], queryFn: fetchDashboard });
  if (dashQuery.isPending) return <Spinner />;
  if (dashQuery.isError || !dashQuery.data) {
    return <ErrorState onRetry={() => void dashQuery.refetch()} />;
  }
  const d: Dashboard = dashQuery.data;
  return (
    <div>
      <PageHeader title="Dashboard" subtitle={formatDateLong(d.date)} />
      <div className="stat-grid" style={{ marginBottom: 16 }}>
        <Stat value={d.todayBookings} label="Booking Hari Ini" />
        <Stat value={d.pendingPayment} label="Menunggu Bayar" />
        <Stat value={d.confirmed} label="Terkonfirmasi" />
        <Stat value={d.completed} label="Selesai" />
        <Stat value={d.cancelled} label="Dibatalkan" />
        <Stat value={d.noShow} label="Tidak Hadir" />
      </div>
      <Card>
        <p style={{ margin: 0 }}>
          <strong>Revenue Hari Ini</strong>{" "}
          <span style={{ fontWeight: 800, color: "var(--teal-700)", fontSize: 20 }}>
            {formatIDR(d.revenueToday)}
          </span>
        </p>
      </Card>
      <h3 style={{ marginTop: 20 }}>Appointment Berikutnya</h3>
      {d.upcoming.length === 0 ? (
        <EmptyState title="Tidak ada appointment hari ini." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {d.upcoming.map((u) => (
            <Card key={u.id} className="list-row">
              <div className="list-row-top">
                <span className="list-row-time">{u.startTime}</span>
                <StatusPill kind="booking" value={u.status} />
              </div>
              <p className="list-row-main" style={{ marginBottom: 0 }}>
                <strong>{u.patientName}</strong> · {u.serviceName} · {u.bookingCode}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Bookings ───────────────────────────
export function AdminBookingsPage() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const listQuery = useQuery({
    queryKey: ["admin", "bookings", { date, status, q }],
    queryFn: () =>
      fetchAdminBookings({
        date: date || undefined,
        status: status || undefined,
        q: q || undefined,
      }),
  });
  const action = useMutation({
    mutationFn: ({
      id,
      act,
      reason,
    }: {
      id: string;
      act: "cancel" | "no-show" | "check-in" | "start" | "complete";
      reason?: string;
    }) => adminBookingAction(id, act, reason),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] }),
  });

  function doAction(b: AdminBooking, act: "cancel" | "no-show") {
    const reason = act === "cancel" ? window.prompt("Alasan pembatalan:") : undefined;
    if (act === "cancel" && reason === null) return;
    if (act === "no-show" && !window.confirm(`Tandai ${b.bookingCode} tidak hadir?`)) return;
    action.mutate({ id: b.id, act, reason: reason ?? undefined });
  }

  return (
    <div>
      <PageHeader title="Bookings" />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <input
          type="date"
          className="input"
          style={{ width: 150 }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <select
          className="input"
          style={{ width: 190 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Semua status</option>
          {BOOKING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          className="input"
          style={{ flex: 1, minWidth: 160 }}
          placeholder="Cari nama pasien…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (listQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title="Tidak ada booking." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {listQuery.data?.map((b: AdminBooking) => (
            <Card key={b.id}>
              <div className="list-row-top">
                <span className="list-row-time">
                  {formatDateShort(b.bookingDate)} {b.startTime}–{b.endTime}
                </span>
                <StatusPill kind="booking" value={b.status} />
              </div>
              <p className="list-row-main">
                <strong>{b.patientProfile.fullName}</strong> · {b.service.name} · {b.therapist.name}
              </p>
              <p className="muted-line" style={{ marginTop: 0 }}>
                {b.bookingCode} · {formatIDR(b.priceSnapshot)}
                {b.payment ? (
                  <>
                    {" · "} <StatusPill kind="payment" value={b.payment.status} />
                  </>
                ) : null}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {b.status === "CONFIRMED" ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => action.mutate({ id: b.id, act: "check-in" })}
                    >
                      Check-in
                    </Button>
                    <Button variant="outline" onClick={() => doAction(b, "no-show")}>
                      No-show
                    </Button>
                  </>
                ) : null}
                {b.status === "CHECKED_IN" ? (
                  <Button
                    variant="outline"
                    onClick={() => action.mutate({ id: b.id, act: "start" })}
                  >
                    Mulai
                  </Button>
                ) : null}
                {b.status === "IN_PROGRESS" ? (
                  <Button onClick={() => action.mutate({ id: b.id, act: "complete" })}>
                    Selesai
                  </Button>
                ) : null}
                {b.status === "PENDING_PAYMENT" || b.status === "CONFIRMED" ? (
                  <Button variant="danger" onClick={() => doAction(b, "cancel")}>
                    Batalkan
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Services ───────────────────────────
export function AdminServicesPage() {
  const queryClient = useQueryClient();
  const listQuery = useQuery({ queryKey: ["admin", "services"], queryFn: fetchServices });
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState(100000);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["admin", "services"] });

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await createService({
        name,
        description: description || undefined,
        durationMinutes: duration,
        price,
      });
      setShowForm(false);
      setName("");
      setDescription("");
      await invalidate();
    } catch (e) {
      setMsg(errMsg(e, "Gagal menyimpan layanan."));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(svc: AdminService) {
    try {
      await updateService(svc.id, { status: svc.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
      await invalidate();
    } catch (e) {
      setMsg(errMsg(e, "Gagal mengubah status."));
    }
  }

  return (
    <div>
      <PageHeader
        title="Layanan"
        action={
          <Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Tutup" : "+ Layanan"}</Button>
        }
      />
      {msg ? <p className="alert alert-error">{msg}</p> : null}
      {showForm ? (
        <Card className="form-stack" style={{ marginBottom: 14 }}>
          <Field
            label="Nama layanan"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Totok Punggung 45 Menit"
          />
          <Field
            label="Deskripsi"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Field
              label="Durasi (menit)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
            <Field
              label="Harga (Rp)"
              type="number"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
            />
          </div>
          <Button onClick={() => void save()} loading={busy}>
            Simpan Layanan
          </Button>
        </Card>
      ) : null}
      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(listQuery.data ?? []).map((s: AdminService) => (
            <Card key={s.id} className="list-row">
              <div className="list-row-top">
                <strong>{s.name}</strong>
                <StatusPill
                  kind={s.status === "ACTIVE" ? "payment" : "payment"}
                  value={s.status === "ACTIVE" ? "PAID" : "EXPIRED"}
                />
              </div>
              <p className="list-row-main" style={{ marginBottom: 6 }}>
                {s.durationMinutes} menit · {formatIDR(s.price)}
                {s._count ? ` · ${s._count.bookings} booking` : ""}
              </p>
              <Button variant="outline" onClick={() => void toggle(s)}>
                {s.status === "ACTIVE" ? "Nonaktifkan" : "Aktifkan"}
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Therapists ───────────────────────────
export function AdminTherapistsPage() {
  const queryClient = useQueryClient();
  const listQuery = useQuery({ queryKey: ["admin", "therapists"], queryFn: fetchAdminTherapists });
  const servicesQuery = useQuery({ queryKey: ["admin", "services"], queryFn: fetchServices });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    specialization: "",
    gender: "" as "" | "L" | "P",
    serviceIds: "",
  });
  const [msg, setMsg] = useState<string | null>(null);
  const [createdPw, setCreatedPw] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "therapists"] });
  };

  async function create() {
    setMsg(null);
    setCreatedPw(null);
    try {
      const serviceIds = form.serviceIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await createTherapist({
        name: form.name,
        email: form.email,
        specialization: form.specialization || undefined,
        gender: form.gender || undefined,
        serviceIds: serviceIds.length ? serviceIds : undefined,
      });
      if (res.initialPassword) setCreatedPw(res.initialPassword);
      setShowForm(false);
      setForm({ name: "", email: "", specialization: "", gender: "", serviceIds: "" });
      await invalidate();
    } catch (e) {
      setMsg(errMsg(e, "Gagal membuat terapis."));
    }
  }

  async function toggle(t: AdminTherapist) {
    try {
      await updateTherapist(t.id, { status: t.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
      await invalidate();
    } catch (e) {
      setMsg(errMsg(e, "Gagal mengubah status."));
    }
  }

  const activeServices = servicesQuery.data ?? [];

  return (
    <div>
      <PageHeader
        title="Therapists"
        action={
          <Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Tutup" : "+ Terapis"}</Button>
        }
      />
      {msg ? <p className="alert alert-error">{msg}</p> : null}
      {createdPw ? (
        <p className="alert" style={{ background: "var(--teal-50)", color: "var(--teal-700)" }}>
          Kata sandi awal terapis: <strong>{createdPw}</strong> — sampaikan ke yang bersangkutan.
        </p>
      ) : null}
      {showForm ? (
        <Card className="form-stack" style={{ marginBottom: 14 }}>
          <Field
            label="Nama lengkap"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Field
            label="Email (untuk login)"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Field
            label="Spesialisasi"
            value={form.specialization}
            onChange={(e) => setForm({ ...form, specialization: e.target.value })}
          />
          <div>
            <label className="label" htmlFor="therapist-gender">
              Jenis kelamin
            </label>
            <select
              id="therapist-gender"
              className="input"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value as "" | "L" | "P" })}
            >
              <option value="">Otomatis dari gelar nama (Pak/Bu)</option>
              <option value="L">Laki-laki</option>
              <option value="P">Perempuan</option>
            </select>
          </div>
          <div>
            <label className="label">Layanan</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
              {activeServices.map((s) => {
                const ids = form.serviceIds
                  .split(",")
                  .map((x) => x.trim())
                  .filter(Boolean);
                const checked = ids.includes(s.id);
                return (
                  <label
                    key={s.id}
                    style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13.5 }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked ? ids.filter((x) => x !== s.id) : [...ids, s.id];
                        setForm({ ...form, serviceIds: next.join(",") });
                      }}
                    />
                    {s.name}
                  </label>
                );
              })}
            </div>
          </div>
          <Button onClick={() => void create()}>Buat Terapis</Button>
        </Card>
      ) : null}
      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(listQuery.data ?? []).map((t: AdminTherapist) => (
            <Card key={t.id}>
              <div className="list-row-top">
                <strong>
                  {t.name} <GenderTag gender={t.gender} />
                </strong>
                <StatusPill kind="payment" value={t.status === "ACTIVE" ? "PAID" : "EXPIRED"} />
              </div>
              <p className="list-row-main" style={{ marginBottom: 6 }}>
                {t.specialization ?? "Terapis"} · {t.user?.email ?? "tanpa akun"} ·{" "}
                {t.services.length} layanan
              </p>
              <p className="muted-line" style={{ margin: "0 0 6px" }}>
                {t.ratingAvg != null ? (
                  <StarDisplay
                    rating={Math.round(t.ratingAvg)}
                    label={`${t.ratingAvg.toFixed(1)} dari 5 · ${t.ratingCount} ulasan pasien`}
                  />
                ) : (
                  "Belum ada penilaian pasien"
                )}
              </p>
              <Button variant="outline" onClick={() => void toggle(t)}>
                {t.status === "ACTIVE" ? "Nonaktifkan" : "Aktifkan"}
              </Button>
              <TherapistReviewsPanel therapistId={t.id} count={t.ratingCount} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Schedule ───────────────────────────
export function AdminSchedulePage() {
  const queryClient = useQueryClient();
  const therapistsQuery = useQuery({
    queryKey: ["admin", "therapists"],
    queryFn: fetchAdminTherapists,
  });
  const [therapistId, setTherapistId] = useState("");
  const [edits, setEdits] = useState<Record<number, { start: string; end: string }>>({});
  const [breaks, setBreaks] = useState<Record<number, { start: string; end: string }>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const scheduleQuery = useQuery({
    queryKey: ["admin", "schedule", therapistId],
    queryFn: () => fetchSchedule(therapistId),
    enabled: !!therapistId,
  });

  const therapists = therapistsQuery.data ?? [];
  const loaded = scheduleQuery.data;

  function selectTherapist(id: string) {
    setTherapistId(id);
    if (loaded) {
      const wh = Object.fromEntries(
        loaded.workingHours.map((w) => [w.dayOfWeek, { start: w.startTime, end: w.endTime }]),
      );
      const br = Object.fromEntries(
        loaded.breaks.map((b) => [b.dayOfWeek, { start: b.startTime, end: b.endTime }]),
      );
      setEdits(wh);
      setBreaks(br);
    }
  }

  async function saveAll() {
    if (!therapistId) return;
    setBusy(true);
    setMsg(null);
    try {
      const workingHours = Object.entries(edits)
        .filter(([, v]) => v.start && v.end)
        .map(([dow, v]) => ({ dayOfWeek: Number(dow), startTime: v.start, endTime: v.end }));
      const breaksArr = Object.entries(breaks)
        .filter(([, v]) => v.start && v.end)
        .map(([dow, v]) => ({
          dayOfWeek: Number(dow),
          startTime: v.start,
          endTime: v.end,
          label: "Istirahat",
        }));
      await saveWeeklySchedule(therapistId, { workingHours, breaks: breaksArr });
      await queryClient.invalidateQueries({ queryKey: ["admin", "schedule", therapistId] });
    } catch (e) {
      setMsg(errMsg(e, "Gagal menyimpan jadwal."));
    } finally {
      setBusy(false);
    }
  }

  async function addOff() {
    const date = window.prompt("Tanggal libur (YYYY-MM-DD):");
    if (!date) return;
    try {
      await addDayOff(therapistId, { date });
      await queryClient.invalidateQueries({ queryKey: ["admin", "schedule", therapistId] });
    } catch (e) {
      setMsg(errMsg(e, "Gagal menambah hari libur."));
    }
  }

  async function delOff(id: string) {
    try {
      await removeDayOff(id);
      await queryClient.invalidateQueries({ queryKey: ["admin", "schedule", therapistId] });
    } catch (e) {
      setMsg(errMsg(e, "Gagal menghapus hari libur."));
    }
  }

  async function addBlock() {
    const date = window.prompt("Tanggal blokir (YYYY-MM-DD):");
    if (!date) return;
    const start = window.prompt("Mulai (HH:mm):", "14:00");
    if (!start) return;
    const end = window.prompt("Selesai (HH:mm):", "15:00");
    if (!end) return;
    const reason = window.prompt("Alasan:") ?? undefined;
    try {
      await addBlockedTime(therapistId, { date, startTime: start, endTime: end, reason });
      await queryClient.invalidateQueries({ queryKey: ["admin", "schedule", therapistId] });
    } catch (e) {
      setMsg(errMsg(e, "Gagal menambah waktu blokir."));
    }
  }

  async function delBlock(id: string) {
    try {
      await removeBlockedTime(id);
      await queryClient.invalidateQueries({ queryKey: ["admin", "schedule", therapistId] });
    } catch (e) {
      setMsg(errMsg(e, "Gagal menghapus waktu blokir."));
    }
  }

  return (
    <div>
      <PageHeader
        title="Jadwal"
        subtitle="Jam kerja mingguan, hari libur & waktu blokir per terapis."
      />
      {msg ? <p className="alert alert-error">{msg}</p> : null}
      <select
        className="input"
        style={{ marginBottom: 14, maxWidth: 320 }}
        value={therapistId}
        onChange={(e) => selectTherapist(e.target.value)}
      >
        <option value="">Pilih terapis…</option>
        {therapists.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>

      {therapistId && scheduleQuery.isPending ? <Spinner /> : null}
      {therapistId && loaded ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card>
            <p style={{ margin: 0, fontWeight: 700 }}>Jam kerja mingguan</p>
            <p className="muted-line">Jam mulai selesai dikunci antara jam buka–tutup lokasi.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {DAYS.map((day, i) => {
                const v = edits[i] ?? { start: "", end: "" };
                const b = breaks[i] ?? { start: "", end: "" };
                return (
                  <div
                    key={day}
                    style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
                  >
                    <span style={{ minWidth: 90, fontWeight: 600, fontSize: 14 }}>{day}</span>
                    <input
                      type="time"
                      className="input"
                      style={{ width: 110 }}
                      value={v.start}
                      onChange={(e) => setEdits({ ...edits, [i]: { ...v, start: e.target.value } })}
                    />
                    <span className="muted-line">s/d</span>
                    <input
                      type="time"
                      className="input"
                      style={{ width: 110 }}
                      value={v.end}
                      onChange={(e) => setEdits({ ...edits, [i]: { ...v, end: e.target.value } })}
                    />
                    <span className="muted-line">· istirahat</span>
                    <input
                      type="time"
                      className="input"
                      style={{ width: 110 }}
                      value={b.start}
                      onChange={(e) =>
                        setBreaks({ ...breaks, [i]: { ...b, start: e.target.value } })
                      }
                    />
                    <input
                      type="time"
                      className="input"
                      style={{ width: 110 }}
                      value={b.end}
                      onChange={(e) => setBreaks({ ...breaks, [i]: { ...b, end: e.target.value } })}
                    />
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12 }}>
              <Button onClick={() => void saveAll()} loading={busy}>
                Simpan Jam Kerja
              </Button>
            </div>
          </Card>

          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p style={{ margin: 0, fontWeight: 700 }}>Hari libur & waktu blokir</p>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="outline" onClick={() => void addOff()}>
                  + Libur
                </Button>
                <Button variant="outline" onClick={() => void addBlock()}>
                  + Blokir
                </Button>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
              {loaded.daysOff.map((d) => (
                <div key={d.id} className="summary-row" style={{ borderBottom: "none" }}>
                  <span>
                    📅 {formatDateLong(d.date)}
                    {d.reason ? ` · ${d.reason}` : ""}
                  </span>
                  <button type="button" className="btn btn-ghost" onClick={() => void delOff(d.id)}>
                    Hapus
                  </button>
                </div>
              ))}
              {loaded.blockedTimes.map((b) => (
                <div key={b.id} className="summary-row" style={{ borderBottom: "none" }}>
                  <span>
                    ⛔ {formatDateLong(b.date)} {b.startTime}–{b.endTime}
                    {b.reason ? ` · ${b.reason}` : ""}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void delBlock(b.id)}
                  >
                    Hapus
                  </button>
                </div>
              ))}
              {loaded.daysOff.length === 0 && loaded.blockedTimes.length === 0 ? (
                <p className="muted-line">Tidak ada.</p>
              ) : null}
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────── Payments ───────────────────────────
export function AdminPaymentsPage() {
  const [status, setStatus] = useState("");
  const listQuery = useQuery({
    queryKey: ["admin", "payments", { status }],
    queryFn: () => fetchAdminPayments({ status: status || undefined }),
  });
  return (
    <div>
      <PageHeader
        title="Pembayaran"
        action={
          <select
            className="input"
            style={{ width: 170 }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Semua status</option>
            <option value="PENDING">PENDING</option>
            <option value="PAID">PAID</option>
            <option value="EXPIRED">EXPIRED</option>
            <option value="FAILED">FAILED</option>
          </select>
        }
      />
      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (listQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title="Belum ada pembayaran." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {listQuery.data?.map((p) => (
            <Card key={p.id}>
              <div className="list-row-top">
                <strong>{p.paymentCode}</strong>
                <StatusPill kind="payment" value={p.status} />
              </div>
              <p className="list-row-main" style={{ marginBottom: 0 }}>
                {formatIDR(p.amount)} · {p.booking.patientProfile.fullName} ·{" "}
                {p.booking.service.name} · {formatDateShort(p.booking.bookingDate)} (
                {p.booking.bookingCode})
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Settings ───────────────────────────
export function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ["admin", "settings"], queryFn: fetchTenantSettings });
  const reportQuery = useQuery({
    queryKey: ["admin", "report", "month"],
    queryFn: () => fetchReport("month"),
  });
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function updateField(patch: Record<string, unknown>) {
    setBusy(true);
    setMsg(null);
    try {
      await updateTenantSettings(patch);
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
    } catch (e) {
      setMsg(errMsg(e, "Gagal menyimpan pengaturan."));
    } finally {
      setBusy(false);
    }
  }

  async function updateQr(ref: string, active: boolean) {
    setBusy(true);
    try {
      await updatePaymentConfig({ merchantReference: ref, isActive: active });
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
    } catch (e) {
      setMsg(errMsg(e, "Gagal menyimpan konfigurasi QRIS."));
    } finally {
      setBusy(false);
    }
  }

  if (settingsQuery.isPending || reportQuery.isPending) return <Spinner />;
  if (settingsQuery.isError || !settingsQuery.data) {
    return <ErrorState onRetry={() => void settingsQuery.refetch()} />;
  }
  const s = settingsQuery.data;
  const report = reportQuery.data;

  return (
    <div>
      <PageHeader title="Pengaturan" subtitle="Lokasi, jam operasional, kebijakan, dan QRIS." />
      {msg ? <p className="alert alert-error">{msg}</p> : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Card className="form-stack">
          <h3 style={{ marginBottom: 0 }}>Jam & kebijakan</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Field
              label="Jam buka"
              type="time"
              value={s.openingTime}
              onChange={(e) => void updateField({ openingTime: e.target.value })}
            />
            <Field
              label="Jam tutup"
              type="time"
              value={s.closingTime}
              onChange={(e) => void updateField({ closingTime: e.target.value })}
            />
            <Field
              label="Interval slot (menit)"
              type="number"
              value={s.slotStepMinutes}
              onChange={(e) => void updateField({ slotStepMinutes: Number(e.target.value) })}
            />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Field
              label="Batas bayar (menit)"
              type="number"
              value={s.paymentExpiryMinutes}
              onChange={(e) => void updateField({ paymentExpiryMinutes: Number(e.target.value) })}
            />
            <Field
              label="Cancel mandiri (jam)"
              type="number"
              value={s.cancelPolicyHours}
              onChange={(e) => void updateField({ cancelPolicyHours: Number(e.target.value) })}
            />
            <Field
              label="Maks booking (hari)"
              type="number"
              value={s.bookingWindowDays}
              onChange={(e) => void updateField({ bookingWindowDays: Number(e.target.value) })}
            />
          </div>
        </Card>

        <Card className="form-stack">
          <h3 style={{ marginBottom: 0 }}>QRIS</h3>
          {(() => {
            const cfg = s.paymentConfigs[0];
            if (!cfg) return null;
            return (
              <>
                <p className="muted-line" style={{ margin: 0 }}>
                  Provider saat ini: <strong>{cfg.provider}</strong> — integrasi provider sungguhan
                  menyusul. Secret tidak pernah disimpan di sisi klien.
                </p>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <Field
                    label="Merchant reference"
                    value={cfg.merchantReference ?? ""}
                    onChange={(e) => void updateQr(e.target.value, cfg.isActive)}
                  />
                  <Button
                    variant="outline"
                    onClick={() => void updateQr(cfg.merchantReference ?? "", !cfg.isActive)}
                  >
                    {cfg.isActive ? "Nonaktifkan QRIS" : "Aktifkan QRIS"}
                  </Button>
                </div>
              </>
            );
          })()}
        </Card>

        {report ? (
          <Card>
            <h3 style={{ marginBottom: 8 }}>Ringkasan 30 hari</h3>
            <div className="stat-grid">
              <Stat value={report.totalBookings} label="Total Booking" />
              <Stat value={report.completed} label="Selesai" />
              <Stat value={report.cancelled} label="Dibatalkan" />
              <Stat value={report.noShow} label="Tidak Hadir" />
            </div>
            <p style={{ margin: "10px 0 0", fontWeight: 800, color: "var(--teal-700)" }}>
              Revenue: {formatIDR(report.revenue)}
            </p>
          </Card>
        ) : null}
        <Button
          variant="outline"
          loading={busy}
          onClick={() => void queryClient.invalidateQueries({ queryKey: ["admin", "settings"] })}
        >
          Muat ulang
        </Button>
      </div>
    </div>
  );
}
